/**
 * Centralized Number Formatting Utilities
 * 
 * This module provides consistent number formatting across the entire application.
 * All numeric display should use these formatters to ensure uniformity.
 * 
 * Formatting Rules:
 * | Data Type    | Decimal Places | Example Output    |
 * |--------------|----------------|-------------------|
 * | Currency     | 2              | ₹1,23,456.78      |
 * | Quantity     | 0              | 1,234             |
 * | Rate/Price   | 2              | 1,234.56          |
 * | Weight       | 3              | 1,234.567         |
 * | Percentage   | 1              | 18.0%             |
 * | BOM Quantity | 3              | 2.500             |
 */

const LOCALE = 'en-IN';

const DEFAULTS = {
  currency:   { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  quantity:   { minimumFractionDigits: 0, maximumFractionDigits: 0 },
  rate:       { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  weight:     { minimumFractionDigits: 3, maximumFractionDigits: 3 },
  percentage: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  bomQty:     { minimumFractionDigits: 3, maximumFractionDigits: 3 },
};

/**
 * Format currency amounts (totals, costs, grand totals, material costs, supplier amounts)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string with ₹ symbol, e.g., "₹1,23,456.78"
 */
export const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return '₹' + num.toLocaleString(LOCALE, DEFAULTS.currency);
};

/**
 * Format quantities (WO qty, JC qty, dispatch qty, stock_in_hand, blocked, issued_qty)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string without decimals, e.g., "1,234"
 */
export const formatQuantity = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString(LOCALE, DEFAULTS.quantity);
};

/**
 * Format rates/prices without currency symbol (per unit price, FIFO price, supplier rate)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string with 2 decimals, e.g., "1,234.56"
 */
export const formatRate = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString(LOCALE, DEFAULTS.rate);
};

/**
 * Format rates/prices with currency symbol (for standalone price display)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string with ₹ symbol, e.g., "₹47.20"
 */
export const formatCurrencyRate = (value) => {
  const num = parseFloat(value) || 0;
  return '₹' + num.toLocaleString(LOCALE, DEFAULTS.rate);
};

/**
 * Format weights (item weight, total weight, scrap weight)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string with 3 decimals, e.g., "12.500"
 */
export const formatWeight = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString(LOCALE, DEFAULTS.weight);
};

/**
 * Format percentages (GST %, completion %, weight %, variance %)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string with % symbol, e.g., "18.0%"
 */
export const formatPercentage = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString(LOCALE, DEFAULTS.percentage) + '%';
};

/**
 * Format BOM quantities (quantity_required in BOM, qty_per_unit, material qty)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string with 3 decimals, e.g., "2.500"
 */
export const formatBomQty = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString(LOCALE, DEFAULTS.bomQty);
};

// Default export for backward compatibility
export default formatCurrency;
